/**
 * FEMA Error Classification System
 * Automatically categorizes and assigns severity to errors for better monitoring
 */

// Type definitions
export interface ErrorClassification {
  category: string;
  severity: 'error' | 'warning' | 'info';
  confidence: number;
  reasons: string[];
}

export interface ClassificationResult {
  category: string;
  confidence: number;
  reasons: string[];
}

export interface SeverityResult {
  severity: 'error' | 'warning' | 'info';
  confidence: number;
  reasons: string[];
}

export interface ErrorInfo {
  message?: string;
  name?: string;
  [key: string]: any;
}

export interface ClassificationOptions {
  category?: string;
  severity?: 'error' | 'warning' | 'info';
  context?: Record<string, any>;
  [key: string]: any;
}

export interface FemaError {
  category?: string;
  severity?: 'error' | 'warning' | 'info';
  [key: string]: any;
}

export interface IgnoreConfig {
  ignoredCategories?: string[];
  ignoredSeverities?: ('error' | 'warning' | 'info')[];
  [key: string]: any;
}

// Default error categories
export const ERROR_CATEGORIES = {
  NETWORK: 'network',
  UI: 'ui',
  ENGINE: 'engine',
  AUTH: 'auth',
  VALIDATION: 'validation',
  PERFORMANCE: 'performance',
  JAVASCRIPT: 'javascript',
  REACT: 'react',
  PROMISE: 'promise',
  RESOURCE: 'resource',
  USER: 'user',
  SERVER: 'server',
  UNKNOWN: 'unknown',
};

// Default severity levels
export const SEVERITY_LEVELS = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Error type patterns for automatic classification
const ERROR_TYPE_PATTERNS = {
  // Network related errors
  [ERROR_CATEGORIES.NETWORK]: [
    /network/i,
    /fetch/i,
    /axios/i,
    /request/i,
    /response/i,
    /timeout/i,
    /connection/i,
    /cors/i,
    /http/i,
  ],
  
  // Authentication related errors
  [ERROR_CATEGORIES.AUTH]: [
    /auth/i,
    /login/i,
    /logout/i,
    /token/i,
    /unauthorized/i,
    /forbidden/i,
    /credential/i,
    /session/i,
  ],
  
  // UI/Component related errors
  [ERROR_CATEGORIES.UI]: [
    /ui/i,
    /component/i,
    /render/i,
    /click/i,
    /focus/i,
    /scroll/i,
    /resize/i,
    /layout/i,
  ],
  
  // Form validation errors
  [ERROR_CATEGORIES.VALIDATION]: [
    /validation/i,
    /validate/i,
    /invalid/i,
    /required/i,
    /format/i,
    /pattern/i,
    /schema/i,
  ],
  
  // Performance related errors
  [ERROR_CATEGORIES.PERFORMANCE]: [
    /performance/i,
    /slow/i,
    /memory/i,
    /leak/i,
    /lag/i,
    /freeze/i,
    /bundle/i,
    /chunk/i,
  ],
  
  // JavaScript runtime errors
  [ERROR_CATEGORIES.JAVASCRIPT]: [
    /javascript/i,
    /js/i,
    /syntax/i,
    /reference/i,
    /type/i,
    /range/i,
    /eval/i,
  ],
  
  // React specific errors
  [ERROR_CATEGORIES.REACT]: [
    /react/i,
    /jsx/i,
    /hook/i,
    /component/i,
    /render/i,
    /state/i,
    /prop/i,
  ],
  
  // Promise/async errors
  [ERROR_CATEGORIES.PROMISE]: [
    /promise/i,
    /async/i,
    /await/i,
    /unhandled.*rejection/i,
    /then/i,
    /catch/i,
  ],
  
  // Resource loading errors
  [ERROR_CATEGORIES.RESOURCE]: [
    /resource/i,
    /load/i,
    /script/i,
    /image/i,
    /css/i,
    /font/i,
    /asset/i,
  ],
  
  // Server errors
  [ERROR_CATEGORIES.SERVER]: [
    /server/i,
    /500/i,
    /502/i,
    /503/i,
    /504/i,
    /internal.*server/i,
    /bad.*gateway/i,
    /service.*unavailable/i,
  ],
};

// Message patterns for severity classification
const SEVERITY_PATTERNS = {
  [SEVERITY_LEVELS.ERROR as 'error' | 'warning' | 'info']: [
    /error/i,
    /fail/i,
    /crash/i,
    /exception/i,
    /fatal/i,
    /critical/i,
    /severe/i,
    /5\d\d/, // 5xx HTTP codes
  ],
  
  [SEVERITY_LEVELS.WARNING as 'error' | 'warning' | 'info']: [
    /warn/i,
    /deprecated/i,
    /slow/i,
    /timeout/i,
    /retry/i,
    /fallback/i,
    /4\d\d/, // 4xx HTTP codes
  ],
  
  [SEVERITY_LEVELS.INFO as 'error' | 'warning' | 'info']: [
    /info/i,
    /debug/i,
    /trace/i,
    /user.*action/i,
    /navigation/i,
    /2\d\d/, // 2xx HTTP codes
  ],
};

/**
 * Classify an error based on type, message, and context
 * @param {string} type - Error type identifier
 * @param {ErrorInfo} errorInfo - Error information
 * @param {ClassificationOptions} options - Additional classification options
 * @returns {ErrorClassification} Classification result with category and severity
 */
export const classifyError = (type: string, errorInfo: ErrorInfo, options: ClassificationOptions = {}): ErrorClassification => {
  const classification: ErrorClassification = {
    category: ERROR_CATEGORIES.UNKNOWN,
    severity: SEVERITY_LEVELS.ERROR as 'error' | 'warning' | 'info',
    confidence: 0,
    reasons: [],
  };
  
  // Use explicit category/severity if provided
  if (options.category) {
    classification.category = options.category;
    classification.confidence = 1.0;
    classification.reasons.push('explicit_category');
  }
  
  if (options.severity) {
    classification.severity = options.severity;
    classification.confidence = Math.max(classification.confidence, 1.0);
    classification.reasons.push('explicit_severity');
  }
  
  // Auto-classify category if not explicitly provided
  if (!options.category) {
    const categoryResult = classifyCategory(type, errorInfo, options);
    classification.category = categoryResult.category;
    classification.confidence = Math.max(classification.confidence, categoryResult.confidence);
    classification.reasons.push(...categoryResult.reasons);
  }
  
  // Auto-classify severity if not explicitly provided
  if (!options.severity) {
    const severityResult = classifySeverity(type, errorInfo, options);
    classification.severity = severityResult.severity;
    classification.confidence = Math.max(classification.confidence, severityResult.confidence);
    classification.reasons.push(...severityResult.reasons);
  }
  
  return classification;
};

/**
 * Classify error category based on patterns
 * @param {string} type - Error type
 * @param {ErrorInfo} errorInfo - Error information
 * @param {ClassificationOptions} options - Additional options
 * @returns {ClassificationResult} Category classification
 */
const classifyCategory = (type: string, errorInfo: ErrorInfo, options: ClassificationOptions): ClassificationResult => {
  const text = `${type} ${errorInfo.message || ''} ${errorInfo.name || ''}`.toLowerCase();
  const context = options.context || {};
  
  let bestMatch: ClassificationResult = {
    category: ERROR_CATEGORIES.UNKNOWN,
    confidence: 0,
    reasons: [],
  };
  
  // Check type patterns
  for (const [category, patterns] of Object.entries(ERROR_TYPE_PATTERNS)) {
    for (const pattern of patterns) {
      if (pattern.test(text)) {
        const confidence = 0.8;
        if (confidence > bestMatch.confidence) {
          bestMatch = {
            category,
            confidence,
            reasons: [`pattern_match:${pattern.source}`],
          };
        }
      }
    }
  }
  
  // Context-based classification
  if (context.url && context.url.includes('/api/')) {
    if (bestMatch.confidence < 0.9) {
      bestMatch = {
        category: ERROR_CATEGORIES.NETWORK,
        confidence: 0.9,
        reasons: ['context:api_url'],
      };
    }
  }
  
  if (context.componentStack) {
    if (bestMatch.confidence < 0.9) {
      bestMatch = {
        category: ERROR_CATEGORIES.REACT,
        confidence: 0.9,
        reasons: ['context:component_stack'],
      };
    }
  }
  
  if (context.status >= 400 && context.status < 500) {
    bestMatch.category = ERROR_CATEGORIES.AUTH;
    bestMatch.confidence = Math.max(bestMatch.confidence, 0.8);
    bestMatch.reasons.push('context:4xx_status');
  }
  
  if (context.status >= 500) {
    bestMatch.category = ERROR_CATEGORIES.SERVER;
    bestMatch.confidence = Math.max(bestMatch.confidence, 0.9);
    bestMatch.reasons.push('context:5xx_status');
  }
  
  // Specific type-based classification
  if (type.includes('validation')) {
    bestMatch.category = ERROR_CATEGORIES.VALIDATION;
    bestMatch.confidence = Math.max(bestMatch.confidence, 0.9);
    bestMatch.reasons.push('type:validation');
  }
  
  if (type.includes('performance')) {
    bestMatch.category = ERROR_CATEGORIES.PERFORMANCE;
    bestMatch.confidence = Math.max(bestMatch.confidence, 0.9);
    bestMatch.reasons.push('type:performance');
  }
  
  if (type.includes('user_action')) {
    bestMatch.category = ERROR_CATEGORIES.USER;
    bestMatch.confidence = Math.max(bestMatch.confidence, 0.9);
    bestMatch.reasons.push('type:user_action');
  }
  
  return bestMatch;
};

/**
 * Classify error severity based on patterns
 * @param {string} type - Error type
 * @param {ErrorInfo} errorInfo - Error information
 * @param {ClassificationOptions} options - Additional options
 * @returns {SeverityResult} Severity classification
 */
const classifySeverity = (type: string, errorInfo: ErrorInfo, options: ClassificationOptions): SeverityResult => {
  const text = `${type} ${errorInfo.message || ''} ${errorInfo.name || ''}`.toLowerCase();
  const context = options.context || {};
  
  let bestMatch: SeverityResult = {
    severity: SEVERITY_LEVELS.ERROR as 'error' | 'warning' | 'info',
    confidence: 0.3, // Default confidence
    reasons: ['default'],
  };
  
  // Check severity patterns
  for (const [severity, patterns] of Object.entries(SEVERITY_PATTERNS)) {
    for (const pattern of patterns) {
      if (pattern.test(text)) {
        const confidence = 0.8;
        if (confidence > bestMatch.confidence) {
          bestMatch = {
            severity: severity as 'error' | 'warning' | 'info',
            confidence,
            reasons: [`pattern_match:${pattern.source}`],
          };
        }
      }
    }
  }
  
  // Context-based severity adjustment
  if (context.status) {
    if (context.status >= 500) {
      bestMatch.severity = SEVERITY_LEVELS.ERROR as 'error' | 'warning' | 'info';
      bestMatch.confidence = Math.max(bestMatch.confidence, 0.9);
      bestMatch.reasons.push('context:server_error');
    } else if (context.status >= 400) {
      bestMatch.severity = SEVERITY_LEVELS.WARNING as 'error' | 'warning' | 'info';
      bestMatch.confidence = Math.max(bestMatch.confidence, 0.8);
      bestMatch.reasons.push('context:client_error');
    } else if (context.status >= 200 && context.status < 300) {
      bestMatch.severity = SEVERITY_LEVELS.INFO as 'error' | 'warning' | 'info';
      bestMatch.confidence = Math.max(bestMatch.confidence, 0.7);
      bestMatch.reasons.push('context:success_status');
    }
  }
  
  // Performance-based severity
  if (type.includes('performance')) {
    const value = context.value;
    const threshold = context.threshold;
    
    if (value && threshold) {
      if (value > threshold * 3) {
        bestMatch.severity = SEVERITY_LEVELS.ERROR as 'error' | 'warning' | 'info';
        bestMatch.confidence = Math.max(bestMatch.confidence, 0.9);
        bestMatch.reasons.push('performance:critical');
      } else if (value > threshold * 2) {
        bestMatch.severity = SEVERITY_LEVELS.WARNING as 'error' | 'warning' | 'info';
        bestMatch.confidence = Math.max(bestMatch.confidence, 0.8);
        bestMatch.reasons.push('performance:warning');
      } else {
        bestMatch.severity = SEVERITY_LEVELS.INFO as 'error' | 'warning' | 'info';
        bestMatch.confidence = Math.max(bestMatch.confidence, 0.7);
        bestMatch.reasons.push('performance:info');
      }
    }
  }
  
  // User action severity (usually info unless it's an error)
  if (type.includes('user_action') && !text.includes('error') && !text.includes('fail')) {
    bestMatch.severity = SEVERITY_LEVELS.INFO as 'error' | 'warning' | 'info';
    bestMatch.confidence = Math.max(bestMatch.confidence, 0.8);
    bestMatch.reasons.push('type:user_action');
  }
  
  // React error boundary errors are always severe
  if (type === 'react_error' && context.componentStack) {
    bestMatch.severity = SEVERITY_LEVELS.ERROR as 'error' | 'warning' | 'info';
    bestMatch.confidence = Math.max(bestMatch.confidence, 0.9);
    bestMatch.reasons.push('type:react_boundary');
  }
  
  return bestMatch;
};

/**
 * Get category statistics for a set of errors
 * @param {FemaError[]} errors - Array of errors
 * @returns {Record<string, number>} Category statistics
 */
export const getCategoryStats = (errors: FemaError[]): Record<string, number> => {
  const stats: Record<string, number> = {};

  errors.forEach(error => {
    const category = error.category || ERROR_CATEGORIES.UNKNOWN;
    stats[category] = (stats[category] || 0) + 1;
  });

  return stats;
};

/**
 * Get severity statistics for a set of errors
 * @param {FemaError[]} errors - Array of errors
 * @returns {Record<string, number>} Severity statistics
 */
export const getSeverityStats = (errors: FemaError[]): Record<string, number> => {
  const stats: Record<string, number> = {};

  errors.forEach(error => {
    const severity = error.severity || SEVERITY_LEVELS.ERROR as 'error' | 'warning' | 'info';
    stats[severity] = (stats[severity] || 0) + 1;
  });

  return stats;
};

/**
 * Check if an error should be ignored based on classification
 * @param {ErrorClassification} classification - Error classification
 * @param {IgnoreConfig} config - Ignore configuration
 * @returns {boolean} True if error should be ignored
 */
export const shouldIgnoreError = (classification: ErrorClassification, config: IgnoreConfig = {}): boolean => {
  const ignoredCategories = config.ignoredCategories || [];
  const ignoredSeverities = config.ignoredSeverities || [];
  
  if (ignoredCategories.includes(classification.category)) {
    return true;
  }
  
  if (ignoredSeverities.includes(classification.severity)) {
    return true;
  }
  
  // Ignore low-confidence classifications in certain categories
  if (classification.confidence < 0.5 && classification.category === ERROR_CATEGORIES.UNKNOWN) {
    return true;
  }
  
  return false;
};

export default {
  classifyError,
  ERROR_CATEGORIES,
  SEVERITY_LEVELS,
  getCategoryStats,
  getSeverityStats,
  shouldIgnoreError,
};
